import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { AnalyticsEventSource, ApprovalDeskPageType, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import { ENABLE_RECENTLY_VISITED_HISTORY } from '@atlassian/cssxp-utils-generic';
import { UserRole, withConfirmationDialog } from '@css-xp/apd-common';
import { AppContent, AppPage, TabPanel, Tabs, useFeatureToggle, useTitle } from '@css-xp/base';
import { COMPLETED_TASKS_TAB, OPEN_TASKS_TAB } from './constants/task';
import { useTaskManager } from './hooks';
import { SIDEBAR_TASK_DETAIL, TaskDetailPage } from './panels';
import { ClosedTasksList, GroupTasksList, MyTasksList } from './task-list';
import './task-manager.scss';

interface PageProps {
  userRole: UserRole | undefined | null;
  title?: string;
}

const allowedSidebars = {
  [SIDEBAR_TASK_DETAIL]: TaskDetailPage,
};

const TaskManager = ({ userRole, title }: PageProps) => {
  const myTasksListRef = useRef<any>();
  const groupTasksListRef = useRef<any>();

  const { taskId } = useParams<{ taskId: string }>();
  const { openTaskSidebar, closeTaskSidebar } = useTaskManager();
  const [isEnabled] = useFeatureToggle();
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(taskId, title);
  }

  const reloadAll = React.useCallback(() => {
    myTasksListRef.current.reload();
    groupTasksListRef?.current?.reload();
  }, []);

  useEffect(() => {
    if (taskId) {
      openTaskSidebar({ taskId, refetch: reloadAll, refetchAll: reloadAll });
    } else {
      closeTaskSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return (
    <AppPage className="apd-app-page" allowedSidebars={allowedSidebars}>
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.APPROVAL_DESK_SCREEN,
          attributes: {
            pageType: ApprovalDeskPageType.TASK_MANAGER,
            role: userRole,
          },
        }}
      />
      <AppContent className="app-content">
        <Tabs className="task-manager-tabs">
          <TabPanel label={OPEN_TASKS_TAB}>
            <MyTasksList ref={myTasksListRef} refetchAll={reloadAll} />
            {userRole !== UserRole.ADVOCATE && <GroupTasksList ref={groupTasksListRef} refetchAll={reloadAll} />}
          </TabPanel>
          <TabPanel label={COMPLETED_TASKS_TAB}>
            <ClosedTasksList />
          </TabPanel>
        </Tabs>
      </AppContent>
    </AppPage>
  );
};

export default withConfirmationDialog(TaskManager);
